import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * Quando qualquer valor muda, o estado de erro é limpo e o conteúdo volta a
   * renderizar. Evita que uma falha antiga fique "fossilizada" numa seção que
   * permanece montada (ex.: abas escondidas).
   */
  resetKeys?: unknown[];
  /** Rótulo usado no log para identificar a área que falhou. */
  label?: string;
  /** Título amigável mostrado na tela de erro. */
  title?: string;
  /** Texto de apoio mostrado na tela de erro. */
  description?: string;
  /** Esconde o botão de recarregar a página inteira. */
  hideReload?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const label = this.props.label ?? 'app';
    console.error(`ErrorBoundary [${label}] caught an error:`, error, errorInfo);
    try {
      sessionStorage.setItem(
        `forja:lastError:${label}`,
        JSON.stringify({
          at: new Date().toISOString(),
          message: error?.message ?? String(error),
          stack: (error?.stack ?? '').split('\n').slice(0, 6).join('\n'),
          componentStack: (errorInfo?.componentStack ?? '').split('\n').slice(0, 8).join('\n'),
        }),
      );
    } catch {}
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError) return;
    const prev = prevProps.resetKeys;
    const next = this.props.resetKeys;
    if (!prev || !next) return;
    const changed = prev.length !== next.length || prev.some((v, i) => !Object.is(v, next[i]));
    if (changed) this.setState({ hasError: false, error: null });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[300px] p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h3 className="font-display text-lg font-semibold text-foreground mb-2">
            {this.props.title ?? 'Algo deu errado'}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mb-6">
            {this.props.description ?? 'Ocorreu um erro inesperado. Tente novamente ou recarregue a página.'}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={this.handleRetry} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Tentar Novamente
            </Button>
            {!this.props.hideReload && (
              <Button onClick={this.handleReload}>
                Recarregar Página
              </Button>
            )}
          </div>
          {this.state.error?.message && (
            <details className="mt-6 max-w-md text-left">
              <summary className="text-xs text-muted-foreground cursor-pointer">Detalhes técnicos</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words text-[10px] text-muted-foreground/80">
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
